// Deep links into the Stripe and Gelato dashboards for orders and customers.

const STRIPE_DASHBOARD = 'https://dashboard.stripe.com';

export type StripeMode = 'test' | 'live';

/**
 * Checkout session ids carry their mode (cs_test_… / cs_live_…); other Stripe
 * ids don't, so fall back to VITE_STRIPE_MODE, then live.
 */
export function stripeMode(checkoutSessionId?: string | null): StripeMode {
  if (checkoutSessionId?.startsWith('cs_test_')) return 'test';
  if (checkoutSessionId?.startsWith('cs_live_')) return 'live';
  return import.meta.env.VITE_STRIPE_MODE === 'test' ? 'test' : 'live';
}

function stripeBase(mode: StripeMode) {
  return mode === 'test' ? `${STRIPE_DASHBOARD}/test` : STRIPE_DASHBOARD;
}

export const stripePaymentUrl = (paymentIntentId: string, mode: StripeMode) =>
  `${stripeBase(mode)}/payments/${paymentIntentId}`;

export const stripeCustomerUrl = (customerId: string, mode: StripeMode) =>
  `${stripeBase(mode)}/customers/${customerId}`;

/** Dashboard search resolves any object id (sessions, charges, …). */
export const stripeSearchUrl = (query: string, mode: StripeMode) =>
  `${stripeBase(mode)}/search?query=${encodeURIComponent(query)}`;

// Gelato doesn't document its dashboard URLs. The orders list is known; the
// order-page pattern is configurable via VITE_GELATO_ORDER_URL ("{id}" is
// replaced) in case Gelato's routing differs.
export const GELATO_ORDERS_URL = 'https://dashboard.gelato.com/orders/list';

export const gelatoOrderUrl = (gelatoOrderId: string) =>
  (import.meta.env.VITE_GELATO_ORDER_URL || 'https://dashboard.gelato.com/orders/{id}').replace(
    '{id}',
    encodeURIComponent(gelatoOrderId)
  );

export const stripePromotionCodeUrl = (promotionCodeId: string, mode: StripeMode) =>
  `${stripeBase(mode)}/promotion_codes/${promotionCodeId}`;

export const stripeDisputeUrl = (disputeId: string, mode: StripeMode) =>
  `${stripeBase(mode)}/disputes/${disputeId}`;
